import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Trash2, Loader2, AlertTriangle } from 'lucide-react';
import type { Agent } from '@/lib/agent-types';

interface AgentDeletionDialogProps {
  agent: Agent;
  onConfirmDelete: () => Promise<void>;
  onCancel: () => void;
}

export function AgentDeletionDialog({ agent, onConfirmDelete, onCancel }: AgentDeletionDialogProps) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onConfirmDelete();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-lg border w-full max-w-sm overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-2.5 px-5 py-4 border-b">
          <div className="flex items-center justify-center size-8 rounded-lg bg-destructive/10">
            <Trash2 className="size-4 text-destructive" />
          </div>
          <div>
            <h2 className="text-sm font-semibold leading-tight">Delete Agent</h2>
            <p className="text-xs text-muted-foreground truncate max-w-[220px]">{agent.name}</p>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-start gap-2 bg-destructive/10 text-xs p-3 rounded-md border border-destructive/20">
            <AlertTriangle className="size-3.5 text-destructive shrink-0 mt-0.5" />
            <span>
              This action permanently deletes the agent and its configuration. This cannot be undone.
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Are you sure you want to delete <span className="font-medium">"{agent.name}"</span>?
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-muted/30">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={deleting}>Cancel</Button>
          <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleting}>
            {deleting ? (
              <>
                <Loader2 className="size-3.5 mr-1.5 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="size-3.5 mr-1.5" />
                Delete Agent
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
